import type { PublicDogEvent, PublicJudge } from '@/types'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { dayEnd, TIME_ZONE } from '@/lib/day'
import { hasExplicitPlacesForClass, isEntryOpen, uniqueClasses } from '@/lib/event'
import { judgeName } from '@/lib/judge'
import { RESTRICTION } from '@/lib/priority'
import { printContactInfo } from '@/lib/utils'
import { getRankingPeriod } from '@/rules_ch'
import CostInfo from '../../components/CostInfo'
import { EntryStatus } from '../../components/EntryStatus'
import { EventDescription } from '../../components/EventDescription'
import { ItemWithCaption } from '../../components/ItemWithCaption'
import { PriorityChips } from '../../components/PriorityChips'
import { TimeLeft } from '../../components/TimeLeft'
import { EventClassPlaces } from './eventInfo/EventClassPlaces'
import { EventClassPlacesHeader } from './eventInfo/EventClassPlacesHeader'
import { EventClassPlacesTotal } from './eventInfo/EventClassPlacesTotal'

interface Props {
  readonly event: PublicDogEvent
}

const judgeClasses = (judge: PublicJudge, event: PublicDogEvent) => {
  const classes = uniqueClasses({
    classes: event.classes.filter((c) =>
      Array.isArray(c.judge) ? c.judge.find((j) => j.id === judge.id) : c.judge?.id === judge.id
    ),
  })
  return classes.length ? ` (${classes.join(', ')})` : ''
}

/** The countdown to the entry's close, while the entry is open and has a day it closes on. */
const EntryTimeLeft = ({ event }: Props) => {
  if (!isEntryOpen(event) || !event.entryEndDate) return null
  return <TimeLeft date={dayEnd(event.entryEndDate, TIME_ZONE)} />
}

export const EventInfo = ({ event }: Props) => {
  const { t } = useTranslation()
  const official = useMemo(() => printContactInfo(event.contactInfo?.official), [event.contactInfo?.official])
  const secretary = useMemo(() => printContactInfo(event.contactInfo?.secretary), [event.contactInfo?.secretary])
  const classes = useMemo(
    () => (event.classes.length ? uniqueClasses({ classes: event.classes }) : [event.eventType]),
    [event.classes, event.eventType]
  )
  // With several classes and no explicit per-class/per-day split, `places` is a shared pool —
  // shown once here instead of (misleadingly) on every class row.
  const showPlacesTotal = classes.length > 1 && classes.some((c) => !hasExplicitPlacesForClass(event, c))
  const judges = useMemo(
    () => event.judges.map((j) => `${judgeName(j, t)}${judgeClasses(j, event)}`).filter(Boolean),
    [event, t]
  )
  const rankingPeriod = getRankingPeriod(
    event.eventType,
    event.entryOrigEndDate ?? event.entryEndDate,
    event.qualificationStartDate
  )

  return (
    <Grid container columnSpacing={1} sx={{ py: 0.5 }}>
      <ItemWithCaption label={t('entryTime')} order={{ xs: 1 }}>
        {t('dateFormat.datespan', { end: event.entryEndDate, start: event.entryStartDate })}
        <EntryStatus event={event} />
        <EntryTimeLeft event={event} />
      </ItemWithCaption>
      {classes.length ? (
        <ItemWithCaption
          label={t('event.classPlaces')}
          order={{ lg: 3, xl: 10, xs: 2 }}
          size={{ lg: 4, md: 6, xl: 'grow', xs: 12 }}
        >
          <EventClassPlacesHeader event={event} />
          {classes.map((c) => (
            <EventClassPlaces key={c} event={event} eventClass={c} />
          ))}
          {showPlacesTotal ? <EventClassPlacesTotal places={event.places} /> : null}
        </ItemWithCaption>
      ) : null}
      {judges.length ? (
        <ItemWithCaption label={t('event.judges')} order={{ lg: 2, xs: 3 }}>
          {judges.map((j) => (
            <Box key={j}>{j}</Box>
          ))}
        </ItemWithCaption>
      ) : null}
      {official ? (
        <ItemWithCaption label={t('event.official')} order={{ lg: 2, xs: 4 }}>
          {official}
        </ItemWithCaption>
      ) : null}
      {secretary ? (
        <ItemWithCaption label={t('event.secretary')} order={{ lg: official ? undefined : 2, xs: 5 }}>
          {secretary}
        </ItemWithCaption>
      ) : null}
      {event.cost ? (
        <ItemWithCaption label={t('paymentDetails')} order={{ xl: 9, xs: 6 }}>
          <CostInfo event={event} />
        </ItemWithCaption>
      ) : null}
      {event.priority?.length ? (
        <ItemWithCaption label={t('event.priority')} order={{ xs: 7 }}>
          <PriorityChips priority={event.priority} />
        </ItemWithCaption>
      ) : null}
      {event.restrictions?.length ? (
        <ItemWithCaption label={t('event.restrictions')} order={{ xs: 7 }}>
          <PriorityChips priority={event.restrictions} options={RESTRICTION} />
        </ItemWithCaption>
      ) : null}
      {event.retrieveType ? (
        <ItemWithCaption label={t('event.retrieveType')} order={{ xs: 7 }}>
          {t(`event.retrieveTypes.${event.retrieveType}`)}
        </ItemWithCaption>
      ) : null}
      {rankingPeriod ? (
        <ItemWithCaption label={t('registration.rankingTime')} order={{ xs: 8 }}>
          {t('dateFormat.datespan', { end: rankingPeriod.maxResultDate, start: rankingPeriod.minResultDate })}
        </ItemWithCaption>
      ) : null}
      <EventDescription event={event} order={{ xs: 9 }} />
    </Grid>
  )
}
